import { AlertDialogBody, AlertDialogFooter, Box, Button, ButtonGroup, Divider, HStack, ModalBody, ModalFooter, Stack, Step, StepDescription, StepIndicator, Stepper, StepSeparator, StepStatus, StepTitle, Text, useDisclosure, useToast } from "@chakra-ui/react"
import { Alert, Dialog, Form, Icon, Modal, Show, Stat, Switcher } from "components"
import { API, Mask } from "config"
import { useDistributor } from "hooks"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { Authenticate } from "screens"

export default function Transfer({ data, ...props }){

    const params = useParams()
    const toast = useToast()
    const distributor = useDistributor(params.id)
    const confirm = useDisclosure()
    const [ step, setStep ] = useState(1)
    const [ customer, setCustomer ] = useState()
    const [ loading, setLoading ] = useState(false)
    const [ cpf, setCpf ] = useState()
    const [ errors, setErrors ] = useState()
    const [ form, setForm ] = useState({
        transfer_cash: true,
        transfer_stock: true
    })

    const handleChangeCpf = (e) => setCpf(Mask.cpf(e.target.value))

    const handleChange = ({ target }) => {
        const { name, value } = target

        switch(name){
            default: setForm({...form, [name]: value}); break;
        }
    }

    const handleSearchCpf = async () => {
        setLoading('search')

        try{
            const callback = await API.get(`/consultants/list?term=${Mask.digit(cpf)}`)
            const result = callback.data?.data?.[0]

            if(!result){ toast({ title: 'O consultor informado não foi encontrado.', status: 'warning'}); }

            if(result){
                setCustomer(result?.customer)
                setForm({...form, name: `Núcleo ${result?.customer?.name} - ${distributor.data?.city?.name}/${distributor.data?.city?.state_code}`})
            }
            
        }catch(err){
            toast({ title: 'Não foi possível buscar o consultor', description: err?.response?.data?.message, status: 'warning'})
        }

        setLoading(false)
    }

    useEffect(() => { if(cpf?.length >=14) handleSearchCpf() }, [cpf])

    return(
    <Modal title="Transferência de núcleo" size="2xl" loading={!!loading} {...props}>
        <ModalBody fontSize="sm">

            <Stepper colorScheme="orange" index={step} mb={8}>

                {/* Introdução */}
                <Step>
                    <StepIndicator>
                        <StepStatus complete={<Icon />} incomplete={<Icon />} active={<Icon />} />
                    </StepIndicator>
                    <Box>
                        <StepTitle>Introdução</StepTitle>
                    </Box>
                    <StepSeparator />
                </Step>

                {/* Informações */}
                <Step>
                    <StepIndicator>
                        <StepStatus complete={<Icon />} incomplete={<Icon />} active={<Icon />} />
                    </StepIndicator>
                    <Box>
                        <StepTitle>Informações</StepTitle>
                        <StepDescription fontSize="xs">Dados do novo núcleo</StepDescription>
                    </Box>
                    <StepSeparator />
                </Step>

                <Step>
                    <StepIndicator>
                        <StepStatus complete={<Icon />} incomplete={<Icon />} active={<Icon />} />
                    </StepIndicator>
                    <Box>
                        <StepTitle>Configurações</StepTitle>
                        <StepDescription fontSize="xs">Estoque e saldo</StepDescription>
                    </Box>
                    <StepSeparator />
                </Step>
            </Stepper>

            {/* Introdução */}
            <Show visible={step===1}>

                <Stack spacing={4}>
                    <Text>Esta é a ferramenta de transferência de núcleo. Ao transferir um núcleo, o sistema irá remover o núcleo atual e criar um novo.</Text>

                    <Alert>
                        O saldo haver e estoque de produtos poderão ser migrados para o novo núcleo na última etapa.
                    </Alert>        

                    <Divider />
                
                    <Form.Form>
                        <Form.Text 
                            required
                            label="CPF do novo responsável:"
                            placeholder="123.456.789.10"
                            helper="Digite o CPF da pessoal no qual deseja transferir este núcleo."
                            size="lg"
                            value={cpf}
                            onChange={handleChangeCpf}
                            isDisabled={!!loading}
                        />
                        
                        <Show visible={!!customer}>
                            <Form.Text
                                label="Novo responsável pelo núcleo:"
                                value={customer?.name}
                                size="lg"
                                isDisabled
                            />
                        </Show>
                    </Form.Form>

                </Stack>
            </Show>

            {/* Dados do novo núcleo */}
            <Show visible={step===2}>

                <Form.Form>

                    <Form.Text
                        required
                        isDisabled
                        label="Novo responsável:"
                        value={customer?.name}
                        size="lg"
                    />

                    <Divider />
                    <Text fontSize="small" color="GrayText" children="Informações do novo núcleo:" />

                    <Form.Text
                        required
                        label="Nome do novo núcleo:"
                        name="name"
                        value={form?.name}
                        error={errors?.name}
                        onChange={handleChange}
                        size="lg"
                        isDisabled={loading}
                        helper={`Nome anterior do núcleo: ${distributor.data?.name}`}
                    />

                    <Alert>
                        Dados como CNPJ, endereço e informações fiscais poderão ser editadas após a transferência.
                    </Alert>

                </Form.Form>

            </Show>

            {/* Final */}
            <Show visible={step===3}>
                <Stack spacing={4}>
                    <Text children="Nesta etapa, selecione as opções que deseja transferir para o novo núcleo:" />

                    <Switcher 
                        title={`Transferir R$ ${Mask.price(distributor.data?.cash_balance)} em saldo haver`} 
                        description={`Atualmente no núcleo ${distributor.data?.name}`}
                        value={form?.transfer_cash}
                        onClick={() => setForm({...form, transfer_cash: !form?.transfer_cash })}
                        isDisabled={loading}
                    />

                    <Switcher 
                        title={`Transferir produtos do estoque`} 
                        description={`Incluir a transferência do estoque anterior.`} 
                        value={form?.transfer_stock}
                        onClick={() => setForm({...form, transfer_stock: !form?.transfer_stock })}
                        isDisabled={loading}
                    />
                </Stack>
            </Show>

        </ModalBody>

        <ModalFooter>
            <ButtonGroup size="sm" isDisabled={!!loading}>

                <Show visible={step===1}>
                    <Button children="Continuar" rightIcon={<Icon name="right" />} isDisabled={!customer} isLoading={loading==='submit'} onClick={() => setStep(2)} />
                </Show>

                <Show visible={step===2}>
                    <Button children="Voltar" leftIcon={<Icon name="left" />} isDisabled={!customer} isLoading={loading==='submit'} onClick={() => setStep(1)} />
                    <Button children="Continuar" rightIcon={<Icon name="right" />} isDisabled={form?.name?.length <= 5} isLoading={loading==='submit'} onClick={() => setStep(3)} />
                </Show>

                <Show visible={step===3}>
                    <Button children="Voltar" leftIcon={<Icon name="left" />} isDisabled={!customer} isLoading={loading==='submit'} onClick={() => setStep(2)} />
                    <Button children="Transferir núcleo" colorScheme="orange" leftIcon={<Icon name="transfer" />} isDisabled={!customer} isLoading={loading==='submit'} onClick={confirm.onOpen} />
                </Show>

                <Button children="Cancelar" colorScheme="red" onClick={props.onClose} />
            </ButtonGroup>
        </ModalFooter>

        <Confirm data={{ distributor, customer, form }} {...confirm} />
    </Modal>
    )
}

const Confirm = ({ data, ...props }) => {
    
    const navigate = useNavigate()
    const toast = useToast()
    const auth = useDisclosure()
    const [ loading, setLoading ] = useState(false)

    const handleSubmit = async () => {
        setLoading(true)

        try{
            const callback = await API.post(`/distributors/${data.distributor.data?.id}/transfer`, {customer_id: data.customer?.id, _method: 'put', ...data.form})
            toast({ title: 'O núcleo foi transferido', status: 'success' })
            navigate(`/distributors/${callback.data?.id}`)
        }catch(err){
            toast({ title: 'Não foi possível transferir o núcleo no momento. Tente novamente', description: err?.response?.data?.message, status: 'warning' })
        }

        setLoading(false)
    }

    return(
    <Dialog title="Confirmar transferência de núcleo" loading={loading} {...props}>
        <AlertDialogBody>
            <Stack spacing={4}>
                <Text fontSize="sm">
                    Você está transferindo o núcleo <b>{data.distributor?.data?.name}</b> para <b>{data.customer?.name}</b>.
                </Text>

                <Alert status="warning">
                    Esta ação é irreversível, tenha certeza do que está fazendo.
                </Alert>
            </Stack>
        </AlertDialogBody>
        <AlertDialogFooter>
            <ButtonGroup size="sm" isDisabled={loading}>
                <Button children="Transferir núcleo" colorScheme="orange" leftIcon={<Icon name="transfer" />} onClick={auth.onOpen} isLoading={loading} />
                <Button children="Cancelar" onClick={props.onClose} />
            </ButtonGroup>
        </AlertDialogFooter>

        <Authenticate onSuccess={handleSubmit} {...auth} />
    </Dialog>
    )
}